const registry = window.searchDropdownRegistry || (window.searchDropdownRegistry = {});

const normalize = (value = '') => value.toString().toLowerCase().trim();

const defaultTexts = {
    placeholder: 'Select an option',
    search: 'Search options',
    empty: 'No results found'
};

const createChevron = () => {
    const chevron = document.createElement('span');
    chevron.className = 'search-dropdown__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.textContent = '▾';
    return chevron;
};

export const initSearchDropdown = (selectId, config = {}) => {
    const selectEl = document.getElementById(selectId);

    if (!selectEl) return null;

    if (registry[selectId]?.destroy) {
        registry[selectId].destroy();
    }

    let host = document.querySelector(`[data-search-root="${selectId}"]`);
    if (!host) {
        host = document.createElement('div');
        host.dataset.searchRootGenerated = selectId;
        if (selectEl.parentElement) {
            selectEl.parentElement.insertBefore(host, selectEl);
        } else {
            document.body.appendChild(host);
        }
    }
    if (!host) return null;

    host.innerHTML = '';
    host.classList.add('search-dropdown-host');

    selectEl.classList.add('sr-only');
    selectEl.setAttribute('tabindex', '-1');
    selectEl.setAttribute('aria-hidden', 'true');
    // Prevent focus/interaction on the hidden native select (avoids "aria-hidden on focused element" warnings)
    // while the custom dropdown is the actual accessible control.
    selectEl.setAttribute('inert', '');

    const state = {
        options: [],
        filtered: [],
        activeIndex: -1,
        isOpen: false,
        searchTerm: '',
        placeholder: config.placeholder || defaultTexts.placeholder,
        searchLabel: config.searchLabel || defaultTexts.search,
        emptyText: config.emptyText || defaultTexts.empty,
        hideSearch: !!config.hideSearch
    };

    const wrapper = document.createElement('div');
    wrapper.className = 'search-dropdown';
    wrapper.dataset.open = 'false';

    const triggerBtn = document.createElement('button');
    triggerBtn.type = 'button';
    triggerBtn.className = 'search-dropdown__trigger';
    triggerBtn.id = `${selectId}-search-trigger`;
    triggerBtn.setAttribute('aria-haspopup', 'listbox');
    triggerBtn.setAttribute('aria-expanded', 'false');
    triggerBtn.setAttribute('aria-label', config?.ariaLabel || state.placeholder);
    triggerBtn.tabIndex = 0;

    const triggerLabel = document.createElement('span');
    triggerLabel.className = 'search-dropdown__label';
    triggerBtn.appendChild(triggerLabel);
    triggerBtn.appendChild(createChevron());

    const panel = document.createElement('div');
    panel.className = 'search-dropdown__panel hidden';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'false');

    const searchWrapper = state.hideSearch ? null : document.createElement('div');
    if (searchWrapper) searchWrapper.className = 'search-dropdown__search';

    const searchInput = state.hideSearch ? null : document.createElement('input');
    if (searchInput) {
        searchInput.type = 'text';
        searchInput.className = 'search-dropdown__input';
        searchInput.placeholder = config.searchPlaceholder || state.searchLabel;
        searchInput.setAttribute('aria-label', state.searchLabel);
        searchInput.setAttribute('role', 'searchbox');
        searchInput.setAttribute('tabindex', '0');
        searchWrapper.appendChild(searchInput);
    }

    const list = document.createElement('ul');
    const listId = `${selectId}-search-options`;
    list.id = listId;
    list.setAttribute('role', 'listbox');
    list.className = 'search-dropdown__options';
    if (state.hideSearch) list.tabIndex = 0;

    triggerBtn.setAttribute('aria-controls', listId);
    if (searchInput) searchInput.setAttribute('aria-controls', listId);

    if (searchWrapper) panel.appendChild(searchWrapper);
    panel.appendChild(list);

    wrapper.appendChild(triggerBtn);
    wrapper.appendChild(panel);
    host.appendChild(wrapper);

    // If there is a <label for="..."> pointing at the native select, repoint it to the custom trigger
    // so clicks/focus land on a focusable, non-aria-hidden element.
    const boundLabel = document.querySelector(`label[for="${selectId}"]`);
    if (boundLabel) {
        boundLabel.setAttribute('for', triggerBtn.id);
    }

    const closeDropdown = () => {
        if (!state.isOpen) return;
        state.isOpen = false;
        panel.classList.add('hidden');
        triggerBtn.setAttribute('aria-expanded', 'false');
        wrapper.dataset.open = 'false';
        if (state.searchTerm && searchInput) {
            state.searchTerm = '';
            searchInput.value = '';
            updateFiltered('');
        }
    };

    const focusActiveOption = () => {
        const optionButtons = list.querySelectorAll('.search-dropdown__option');
        const target = optionButtons[state.activeIndex] || optionButtons[0] || null;
        if (target) {
            target.focus();
            return;
        }
        if (state.hideSearch) {
            list.focus();
        }
    };

    const openDropdown = () => {
        if (state.isOpen) return;
        state.isOpen = true;
        panel.classList.remove('hidden');
        triggerBtn.setAttribute('aria-expanded', 'true');
        wrapper.dataset.open = 'true';
        requestAnimationFrame(() => {
            if (searchInput) {
                searchInput.focus();
                return;
            }
            focusActiveOption();
        });
    };

    const handleDocumentClick = event => {
        if (!host.contains(event.target)) {
            closeDropdown();
        }
    };

    document.addEventListener('mousedown', handleDocumentClick);

    const highlightActive = () => {
        const optionButtons = list.querySelectorAll('.search-dropdown__option');
        optionButtons.forEach((btn, idx) => {
            if (idx === state.activeIndex) {
                btn.classList.add('search-dropdown__option--active');
                btn.setAttribute('aria-selected', 'true');
            } else {
                btn.classList.remove('search-dropdown__option--active');
                btn.setAttribute('aria-selected', 'false');
            }
        });
    };

    const interceptUnavailable = (option, optionBtn) => {
        if (!option?.unavailable || typeof config.onUnavailableClick !== 'function') return false;
        config.onUnavailableClick(option, optionBtn);
        closeDropdown();
        triggerBtn.focus();
        return true;
    };

    const selectOption = option => {
        if (!option || option.disabled) return;
        if (interceptUnavailable(option, list.querySelector(`.search-dropdown__option[data-value="${option.value}"]`))) {
            return;
        }
        if (selectEl.value !== option.value) {
            selectEl.value = option.value;
            selectEl.dispatchEvent(new Event('change', { bubbles: true }));
        }
        closeDropdown();
        triggerBtn.focus();
    };

    const renderOptions = () => {
        list.innerHTML = '';
        if (!state.filtered.length) {
            const emptyLi = document.createElement('li');
            emptyLi.className = 'search-dropdown__empty';
            emptyLi.textContent = state.emptyText;
            list.appendChild(emptyLi);
            state.activeIndex = -1;
            return;
        }

        state.filtered.forEach((option, idx) => {
            const li = document.createElement('li');
            li.setAttribute('role', 'presentation');

            const optionBtn = document.createElement('button');
            optionBtn.type = 'button';
            optionBtn.dataset.value = option.value;
            optionBtn.setAttribute('role', 'option');
            optionBtn.className = 'search-dropdown__option';
            optionBtn.textContent = option.label;

            if (option.disabled) {
                optionBtn.disabled = true;
                optionBtn.classList.add('search-dropdown__option--disabled');
            }
            if (option.unavailable) {
                optionBtn.classList.add('search-dropdown__option--unavailable');
                optionBtn.setAttribute('data-unavailable', 'true');
                optionBtn.title = option.title || 'This outfit is not available for this character';
                if (typeof config.onUnavailableClick === 'function') {
                    optionBtn.classList.add('search-dropdown__option--disabled');
                    optionBtn.setAttribute('aria-disabled', 'true');
                }
            }

            optionBtn.addEventListener('click', () => {
                if (interceptUnavailable(option, optionBtn)) return;
                selectOption(option);
            });
            optionBtn.addEventListener('mouseenter', () => {
                state.activeIndex = idx;
                highlightActive();
            });

            li.appendChild(optionBtn);
            list.appendChild(li);
        });

        highlightActive();
    };

    const updateFiltered = term => {
        const query = normalize(term);
        state.searchTerm = query;
        state.filtered = !query
            ? [...state.options]
            : state.options.filter(opt => opt.labelLower.includes(query));

        const currentIndex = state.filtered.findIndex(
            opt => opt.value === selectEl.value
        );
        state.activeIndex = currentIndex >= 0 ? currentIndex : 0;
        renderOptions();
        highlightActive();
    };

    const refreshOptions = () => {
        if (!state.placeholder) {
            state.placeholder = defaultTexts.placeholder;
        }
        state.options = Array.from(selectEl.options).map(opt => ({
            value: opt.value,
            label: opt.textContent.trim(),
            labelLower: normalize(opt.textContent),
            disabled: opt.disabled,
            unavailable: opt.hasAttribute('data-unavailable'),
            title: opt.getAttribute('title') || ''
        }));
        if (!state.options.length) return;
        updateFiltered(state.searchTerm);
        const selected = state.options.find(opt => opt.value === selectEl.value);
        const display = selected?.label || state.placeholder;
        triggerLabel.textContent = display;
        triggerLabel.classList.toggle('text-white/60', !selected);
        triggerLabel.classList.toggle('search-dropdown__label--unavailable', !!selected?.unavailable);
    };

    const handleInputKey = event => {
        if (!state.filtered.length) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            state.activeIndex =
                (state.activeIndex + 1) % state.filtered.length;
            highlightActive();
            if (state.hideSearch) focusActiveOption();
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            state.activeIndex =
                (state.activeIndex - 1 + state.filtered.length) %
                state.filtered.length;
            highlightActive();
            if (state.hideSearch) focusActiveOption();
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            selectOption(state.filtered[state.activeIndex]);
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            closeDropdown();
            triggerBtn.focus();
        }
    };

    const handleTriggerKey = event => {
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openDropdown();
        }
    };

    triggerBtn.addEventListener('click', () => {
        if (state.isOpen) {
            closeDropdown();
        } else {
            openDropdown();
        }
    });

    triggerBtn.addEventListener('keydown', handleTriggerKey);
    if (searchInput) {
        searchInput.addEventListener('input', e => updateFiltered(e.target.value));
        searchInput.addEventListener('keydown', handleInputKey);
    } else {
        panel.addEventListener('keydown', handleInputKey);
    }

    selectEl.addEventListener('change', () => {
        const selected = state.options.find(opt => opt.value === selectEl.value);
        const display = selected?.label || state.placeholder;
        triggerLabel.textContent = display;
        triggerLabel.classList.toggle('text-white/60', !selected);
        triggerLabel.classList.toggle('search-dropdown__label--unavailable', !!selected?.unavailable);
    });

    // Safety: if something still tries to focus the hidden select, redirect focus to the trigger.
    selectEl.addEventListener('focus', () => {
        triggerBtn.focus();
    });

    const mutationObserver = new MutationObserver(() => {
        refreshOptions();
    });
    mutationObserver.observe(selectEl, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-unavailable', 'disabled', 'title']
    });

    refreshOptions();

    const instance = {
        refresh: newConfig => {
            if (newConfig?.placeholder) {
                state.placeholder = newConfig.placeholder;
            }
            if (newConfig?.searchLabel) {
                state.searchLabel = newConfig.searchLabel;
                searchInput.setAttribute('aria-label', state.searchLabel);
                searchInput.placeholder =
                    newConfig.searchPlaceholder || state.searchLabel;
            }
            refreshOptions();
        },
        getSelectedLabel: () => {
            const selected = state.options.find(opt => opt.value === selectEl.value);
            return selected?.label || '';
        },
        destroy: () => {
            document.removeEventListener('mousedown', handleDocumentClick);
            mutationObserver.disconnect();
            host.innerHTML = '';
        }
    };

    registry[selectId] = instance;
    return instance;
};

